import { useMemo } from "react";
import type { ImportAddressFix } from "@adva/types";
import { ClusteredMap } from "@/components/map/clustered-map";
import { EmptyState } from "@/components/v2/empty-state";

interface MapTabProps {
  fixes: ImportAddressFix[];
}

interface FeatureProps {
  fixId: string;
  address: string;
  precision: string;
  isFailed: boolean;
}

export function MapTab({ fixes }: MapTabProps) {
  const geocodedFixes = useMemo(
    () =>
      fixes.filter(
        (f) =>
          typeof f.latitude === "number" &&
          typeof f.longitude === "number" &&
          !Number.isNaN(f.latitude) &&
          !Number.isNaN(f.longitude),
      ),
    [fixes],
  );

  const failedFixes = useMemo(() => fixes.filter((f) => f.is_failed), [fixes]);

  const geojson = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features: geocodedFixes.map((f) => {
        const props: FeatureProps = {
          fixId: f.id,
          address:
            f.formatted_address ?? f.original_input ?? "(unknown address)",
          precision: f.precision_type ?? "unknown",
          isFailed: f.is_failed,
        };
        return {
          type: "Feature" as const,
          properties: props,
          geometry: {
            type: "Point" as const,
            coordinates: [f.longitude as number, f.latitude as number],
          },
        };
      }),
    }),
    [geocodedFixes],
  );

  if (fixes.length === 0) {
    return (
      <EmptyState
        title="No addresses to map"
        description="None of the imported records have geocoded coordinates yet."
      />
    );
  }

  if (geocodedFixes.length === 0) {
    return (
      <EmptyState
        title="No geocoded coordinates"
        description={`All ${fixes.length} address ${fixes.length === 1 ? "fix" : "fixes"} are missing latitude/longitude — none can be plotted on a map.`}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4 typo-label text-[color:var(--color-text-md)]">
        <span className="inline-flex items-center gap-1.5">
          <span
            className="size-2 rounded-full bg-[color:var(--color-accent)]"
            aria-hidden="true"
          />
          {geocodedFixes.length - failedFixes.length} geocoded
        </span>
        {failedFixes.length > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <span
              className="size-2 rounded-full bg-[color:var(--color-danger)]"
              aria-hidden="true"
            />
            {failedFixes.length} failed
          </span>
        )}
      </div>
      <div className="h-[500px] overflow-hidden rounded-md border border-[color:var(--color-border)]">
        <ClusteredMap geojsonData={geojson} className="h-full w-full" />
      </div>
    </div>
  );
}
